// Split a PostgREST `.in()` list into bounded pieces.
//
// `in.()` filters ride in the GET URL, so the request line grows with the
// list. Past roughly 16 KB proxies start refusing it — and supabase-js reports
// that as a resolved `{ data: null, error }`, so a caller that binds only
// `data` turns a refused request into an authoritative empty result. That is
// exactly how the Anchor scan station spent twelve days showing bare worker
// numbers instead of packer names: 409 seconded workers went out as a single
// 16,119-character filter and the error was discarded (scan-push, #2099).
//
// Budget per item, including the `%2C` separator:
//
//   uuid            ~39 chars  ⇒ ~410 items reaches 16 KB
//   worker_number   ~7 chars   ⇒ ~2,200 items
//
// A list bounded by a SCREEN (one selection, one consignment) is safe forever
// and does not need this. A list bounded by a TENANT — a whole roster, every
// pallet on a run — is a cliff that arrives at whatever size the biggest
// customer reaches, on their site, silently. Chunk those.
//
// Edge functions cannot import from src/; `supabase/functions/_shared/
// faceNames.ts` carries the same constant for that side.

import type { PageResult } from './fetchAllPaged'
import { fetchAllPaged } from './fetchAllPaged'

export const IN_CHUNK = 100

/** Split `items` into consecutive pieces of at most `size`. Empty in, empty out. */
export function inChunks<T>(items: readonly T[], size: number = IN_CHUNK): T[][] {
  const step = Math.max(1, size)
  const out: T[][] = []
  for (let i = 0; i < items.length; i += step) out.push(items.slice(i, i + step))
  return out
}

/**
 * `fetchAllPaged` for a read filtered by a long `.in()` list: drains every
 * chunk's pages and concatenates. `makePage` receives the chunk to filter on
 * plus the page window, and must build a FRESH query each call (PostgREST
 * builders are single-use).
 *
 * Errors behave like `fetchAllPaged`'s: the first one stops the drain and is
 * returned alongside whatever was already read, so the caller — not this
 * helper — decides whether partial data is usable. An empty `values` list
 * short-circuits to no rows without issuing a request, which matters because
 * `.in('x', [])` is a query that matches nothing but still costs a round trip.
 *
 * `concurrency` (default 1: one chunk at a time) drains up to that many chunks
 * at once. Rows still come back in CHUNK order, and an error still stops it:
 * no new chunk starts once one has failed, and the result is the rows of the
 * chunks before the first failed one plus that chunk's own pages — exactly
 * what the one-at-a-time drain would have returned.
 */
export async function fetchAllPagedIn<T, V>(
  values: readonly V[],
  makePage: (chunk: V[], from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: { pageSize?: number; maxRows?: number; chunkSize?: number; concurrency?: number } = {},
): Promise<{ rows: T[]; error: string | null }> {
  if (values.length === 0) return { rows: [], error: null }
  const chunks = inChunks(values, opts.chunkSize ?? IN_CHUNK)
  const results: ({ rows: T[]; error: string | null } | undefined)[] = new Array(chunks.length)
  let next = 0
  let failed = false
  const worker = async () => {
    while (!failed && next < chunks.length) {
      const i = next++
      // paged-order-ignore makePage is the CALLER's page factory — the order is
      // theirs to impose and dal:paged-order judges it at their call site.
      const page = await fetchAllPaged<T>((from, to) => makePage(chunks[i], from, to), opts)
      results[i] = page
      if (page.error) failed = true
    }
  }
  const lanes = Math.max(1, Math.min(opts.concurrency ?? 1, chunks.length))
  await Promise.all(Array.from({ length: lanes }, worker))
  const rows: T[] = []
  for (const page of results) {
    if (!page) break                      // never started: an earlier chunk failed
    rows.push(...page.rows)
    if (page.error) return { rows, error: page.error }
  }
  return { rows, error: null }
}
